// Cart Drawer Web Component
class CartDrawerComponent extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector('[ref="dialog"]');
    this.closeButton = this.querySelector('[ref="closeButton"]');
    
    this.init();
  }

  init() {
    // Close cart drawer - use event delegation to handle dynamically added buttons
    this.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('[ref="closeButton"], .cart-drawer__close-button');
      if (closeBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      }
    });

    // ✅ IMPORTANT: Allow theme quick-add modal to work
    // Close drawer AFTER the theme handler runs (capture + setTimeout)
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.quick-add-btn');
      if (!btn) return;

      // only if this click is inside THIS cart drawer
      const drawer = btn.closest('cart-drawer-component');
      if (!drawer || drawer !== this) return;

      // Do NOT preventDefault/stopPropagation here.
      // Let the theme open the quick-add modal, then close the drawer.
      setTimeout(() => {
        try {
          if (this.dialog && this.dialog.open) this.close();
        } catch (_) {}
      }, 0);
    }, true);

    // Close on backdrop click
    if (this.dialog) {
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) {
          this.close();
        }
      });
    }

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.dialog && this.dialog.open) {
        this.close();
      }
    });

    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      this.refreshCart();
    });

    // Auto-open if attribute is set and item was just added
    if (this.hasAttribute('auto-open')) {
      document.addEventListener('cart:item-added', () => {
        this.open();
      });
    }
  }

  open() {
    if (this.dialog) {
      this.dialog.showModal();
      document.body.style.overflow = 'hidden';
      this.dialog.classList.add('active');
      this.classList.add('is-open');
      // Trigger animation
      setTimeout(() => {
        this.dialog.style.transform = 'translateX(0)';
      }, 10);
      
      // Show recommendations sidebar on desktop
      const recommendationsSidebar = this.querySelector('.cart-recommendations-desktop .cart-recommendations');
      if (recommendationsSidebar) {
        setTimeout(() => {
          recommendationsSidebar.style.transform = 'translateX(0)';
        }, 150);
      }
    }
  }

  close() {
    if (this.dialog) {
      // Hide recommendations sidebar
      const recommendationsSidebar = this.querySelector('.cart-recommendations-desktop .cart-recommendations');
      if (recommendationsSidebar) {
        recommendationsSidebar.style.transform = 'translateX(100%)';
      }
      
      this.dialog.style.transform = 'translateX(100%)';
      setTimeout(() => {
        this.dialog.close();
        document.body.style.overflow = '';
        this.dialog.classList.remove('active');
        this.classList.remove('is-open');
      }, 300);
    }
  }

  async refreshCart() {
    // Fetch fresh cart data
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update the entire cart drawer content
      const sectionResponse = await fetch('/?section_id=cart-drawer');
      const html = await sectionResponse.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newContent = doc.querySelector('cart-items-component');
      
      const cartItemsComponent = this.querySelector('cart-items-component');
      if (newContent && cartItemsComponent) {
        cartItemsComponent.innerHTML = newContent.innerHTML;
      }
      
      // Update cart icon count
      this.updateCartIcon();
      
      // If cart is empty, show empty state
      if (cart.item_count === 0) {
        this.dialog.classList.add('cart-drawer--empty');
      } else {
        this.dialog.classList.remove('cart-drawer--empty');
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
      // Fallback: reload the page
      window.location.reload();
    }
  }

  updateCartIcon() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Update count in all cart count elements
        document.querySelectorAll('.jsccount, .cart-count').forEach(el => {
          el.textContent = cart.item_count;
          el.style.opacity = cart.item_count > 0 ? '1' : '0';
        });

        // Update total price
        document.querySelectorAll('.js_cat_ttprice').forEach(el => {
          const moneyFormat = window.theme && window.theme.moneyFormat ? window.theme.moneyFormat : '{{amount}}';
          el.textContent = Shopify.formatMoney(cart.total_price, moneyFormat);
        });
      });
  }
}

// Cart Items Component
class CartItemsComponent extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
  }

  async refresh() {
    try {
      const response = await fetch(`${window.location.pathname}?section_id=${this.sectionId || 'cart-drawer'}`);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newContent = doc.querySelector('cart-items-component');
      
      if (newContent) {
        this.innerHTML = newContent.innerHTML;
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }
}

// Scroll Hint Component
class ScrollHint extends HTMLElement {
  connectedCallback() {
    this.setupScrollHint();
  }

  setupScrollHint() {
    const hasOverflow = this.scrollHeight > this.clientHeight;
    if (hasOverflow) {
      this.classList.add('has-scroll');
    }
  }
}

// Cart Icon Component
class CartIconComponent extends HTMLElement {
  connectedCallback() {
    this.updateCount();
    document.addEventListener('cart:updated', () => this.updateCount());
  }

  updateCount() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const countElement = this.querySelector('.cart-count');
        if (countElement) {
          countElement.textContent = cart.item_count;
          countElement.style.display = cart.item_count > 0 ? 'block' : 'none';
        }
      });
  }
}

// Register custom elements
if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}

if (!customElements.get('scroll-hint')) {
  customElements.define('scroll-hint', ScrollHint);
}

if (!customElements.get('cart-icon-component')) {
  customElements.define('cart-icon-component', CartIconComponent);
}

// Helper function for adding items to cart
window.addToCart = async function(formData) {
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    });

    if (response.ok) {
      const item = await response.json();
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: item }));
      document.dispatchEvent(new CustomEvent('cart:updated'));
      
      // Open cart drawer automatically
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer) {
        cartDrawer.open();
      }
      
      return item;
    } else {
      throw new Error('Failed to add item to cart');
    }
  } catch (error) {
    console.error('Error adding to cart:', error);
    throw error;
  }
};

// Override the theme's add to cart behavior - MUST RUN AFTER nt_settings loads
(function() {
  let settingsOverridden = false;
  
  const overrideSettings = function() {
    if (typeof nt_settings !== 'undefined' && !settingsOverridden) {
      // Force disable popup by setting after_action_atc to '0'
      Object.defineProperty(nt_settings, 'after_action_atc', {
        value: '0',
        writable: false,
        configurable: true
      });
      settingsOverridden = true;
      console.log('Cart Drawer: Popup permanently disabled via Object.defineProperty');
    }
  };
  
  // Try multiple times to ensure we override after nt_settings loads
  const tryOverride = setInterval(function() {
    overrideSettings();
    if (settingsOverridden) {
      clearInterval(tryOverride);
    }
  }, 50);
  
  // Stop trying after 5 seconds
  setTimeout(function() {
    clearInterval(tryOverride);
  }, 5000);
  
  // Override the global TriggerAfterATC function
  const originalTriggerAfterATC = window.TriggerAfterATC;
  window.TriggerAfterATC = function(bl, id) {
    console.log('TriggerAfterATC intercepted - opening drawer instead of popup');
    // Instead of showing popup, open cart drawer
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && bl) {
      cartDrawer.refreshCart().then(() => {
        cartDrawer.open();
      });
    }
  };
  
  // Block magnificPopup from opening for cart actions
  if (typeof jQuery !== 'undefined') {
    const originalMagnificPopup = jQuery.fn.magnificPopup;
    jQuery.fn.magnificPopup = function(options) {
      // Check if this is a cart-related popup
      if (options && options.items && options.items.src) {
        const src = options.items.src;
        if (src.includes('add-to-cart-popup') || src.includes('popup_added_to_cart')) {
          console.log('Cart Drawer: Blocked magnificPopup cart popup');
          // Open drawer instead
          const cartDrawer = document.querySelector('cart-drawer-component');
          if (cartDrawer) {
            cartDrawer.refreshCart().then(() => {
              cartDrawer.open();
            });
          }
          
          // Remove loading class from add to cart buttons
          setTimeout(() => {
            document.querySelectorAll('.js_add_ld.loading').forEach(btn => {
              btn.classList.remove('loading');
            });
          }, 300);
          
          return this; // Return early without opening popup
        }
      }
      // For non-cart popups, use original function
      return originalMagnificPopup.apply(this, arguments);
    };
    
    // Copy over the static methods
    jQuery.magnificPopup = originalMagnificPopup;
  }
  
  // Listen for successful cart additions from the theme
  document.addEventListener('cart:requestComplete', function(e) {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer) {
      cartDrawer.refreshCart().then(() => {
        cartDrawer.open();
      });
    }
  });
  
  // Also listen for AJAX cart success
  if (typeof jQuery !== 'undefined') {
    jQuery(document).on('ajaxComplete', function(event, xhr, settings) {
      if (settings.url && (settings.url.includes('/cart/add') || settings.url.includes('/cart/update'))) {
        console.log('Cart Drawer: AJAX cart operation detected');
        setTimeout(function() {
          const cartDrawer = document.querySelector('cart-drawer-component');
          if (cartDrawer) {
            cartDrawer.refreshCart().then(() => {
              cartDrawer.open();
            });
          }
        }, 150);
      }
    });
    
    // Also listen for theme-specific events
    jQuery(document).on('CartUpdateSuccess CartChangeSuccess', function() {
      console.log('Cart Drawer: Theme cart event detected');
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer) {
        cartDrawer.refreshCart().then(() => {
          cartDrawer.open();
        });
      }
      
      // Remove loading class from add to cart buttons - CRITICAL FIX
      setTimeout(() => {
        document.querySelectorAll('.js_add_ld.loading, .js_addtc.loading, button.loading, .btn.loading').forEach(btn => {
          btn.classList.remove('loading');
        });
      }, 300);
    });
    
    // Also watch for any button getting stuck with loading class
    setInterval(() => {
      // Remove loading from visible buttons after 3 seconds
      document.querySelectorAll('.js_add_ld.loading, .js_addtc.loading').forEach(btn => {
        const hasLoading = btn.classList.contains('loading');
        const loadingTime = btn.dataset.loadingTime;
        
        if (hasLoading) {
          if (!loadingTime) {
            btn.dataset.loadingTime = Date.now();
          } else if (Date.now() - parseInt(loadingTime) > 3000) {
            btn.classList.remove('loading');
            delete btn.dataset.loadingTime;
          }
        } else {
          delete btn.dataset.loadingTime;
        }
      });
    }, 500);
  }
})();

// Helper function for updating cart
window.updateCart = async function(updates) {
  try {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates })
    });

    if (response.ok) {
      const cart = await response.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      return cart;
    } else {
      throw new Error('Failed to update cart');
    }
  } catch (error) {
    console.error('Error updating cart:', error);
    throw error;
  }
};
